import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { DownloadSimple, Trash } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function ReconciliationPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [empSearch, setEmpSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/reports/documents").then((r) => setData(r.data))
    .catch((e) => { toast.error(formatApiError(e)); setData({ totals: {}, employees: [], pool_by_topic: [], pool_by_kind: [], junk_candidates: [] }); });

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const employees = useMemo(() => {
    let f = data?.employees || [];
    if (empSearch.trim()) {
      const q = empSearch.trim().toLowerCase();
      f = f.filter((r) => (r.name || "").toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    return f;
  }, [data, empSearch]);

  const dismissJunk = async () => {
    const ids = (data?.junk_candidates || []).map((j) => j.id);
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api.post("/inbox/pool/bulk-dismiss", { ids });
      toast.success(`Dismissed ${r.data.dismissed} junk file${r.data.dismissed === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const exportCsv = () => {
    const header = "Employee,Email,Certificates,Signed submissions,Other evidence,Employment records,Total";
    const lines = employees.map((r) => [
      `"${(r.name || "").replace(/"/g, '""')}"`, r.email, r.certificates, r.submissions, r.other_evidence, r.employment_records, r.total,
    ].join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `document-reconciliation-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!isAdmin) {
    return <div className="py-16 text-center text-sm text-stone-500" data-testid="recon-admin-only">Document reconciliation is available to admins only.</div>;
  }

  const t = data?.totals || {};

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900" data-testid="recon-title">Document Reconciliation</h1>
          <p className="mt-1 text-sm text-stone-500">Every file accounted for — attached to an employee, or waiting in the unmatched pool. Signed submissions are the proof of training; certificates are the souvenir.</p>
        </div>
        <Button onClick={exportCsv} disabled={!employees.length} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="recon-export-button">
          <DownloadSimple size={15} className="mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {[
          ["Total attached", t.attached, "recon-total-attached", "text-stone-900"],
          ["Certificates", t.certificates, "recon-total-certificates", "text-stone-500"],
          ["Signed submissions (proof)", t.submissions, "recon-total-submissions", "text-[#1e8449]"],
          ["Other evidence", t.other_evidence, "recon-total-other", "text-stone-700"],
          ["Employment records", t.employment_records, "recon-total-employment", "text-stone-700"],
          ["Unmatched pool", t.pool_pending, "recon-total-pool", t.pool_pending > 0 ? "text-[#8a5d14]" : "text-[#1e8449]"],
        ].map(([label, val, testid, cls]) => (
          <div key={testid} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={testid}>
            <p className={`font-heading text-2xl font-bold ${cls}`}>{val ?? "—"}</p>
            <p className="text-xs text-stone-500">{label}</p>
          </div>
        ))}
      </div>

      {data === null ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="recon-pool-by-topic">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Unmatched pool by training</h2>
              {data.pool_by_topic.length === 0 ? (
                <p className="mt-2 text-sm text-stone-400" data-testid="recon-pool-topic-empty">Pool is empty — everything is attached.</p>
              ) : (
                <div className="mt-2 divide-y divide-stone-100">
                  {data.pool_by_topic.map((r, i) => (
                    <div key={r.title} className="flex items-center justify-between py-1.5 text-sm" data-testid={`recon-pool-topic-${i}`}>
                      <span className="text-stone-700">{r.title}</span>
                      <span className="font-medium text-stone-900">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm" data-testid="recon-pool-by-kind">
                <h2 className="font-heading text-lg font-semibold text-stone-900">Unmatched pool by type</h2>
                {data.pool_by_kind.length === 0 ? (
                  <p className="mt-2 text-sm text-stone-400">Nothing pending.</p>
                ) : (
                  <div className="mt-2 divide-y divide-stone-100">
                    {data.pool_by_kind.map((r, i) => (
                      <div key={r.kind} className="flex items-center justify-between py-1.5 text-sm" data-testid={`recon-pool-kind-${i}`}>
                        <span className="text-stone-700">{r.kind}</span>
                        <span className="font-medium text-stone-900">{r.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {data.junk_candidates.length > 0 && (
                <div className="rounded-lg border border-[#B7791F]/40 bg-white p-5 shadow-sm" data-testid="recon-junk-section">
                  <div className="flex items-center justify-between gap-2">
                    <h2 className="font-heading text-lg font-semibold text-stone-900">Suggested junk ({data.junk_candidates.length})</h2>
                    <Button onClick={dismissJunk} disabled={busy} variant="outline"
                      className="h-8 border-[#C0392B]/40 px-3 text-xs text-[#C0392B] hover:bg-[#C0392B] hover:text-white" data-testid="recon-junk-dismiss-all">
                      <Trash size={13} className="mr-1" /> {busy ? "Dismissing…" : "Dismiss all"}
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-stone-400">Test submissions and placeholder files detected by name. Review before dismissing — dismissed files stay out of the pool.</p>
                  <div className="mt-2 max-h-48 overflow-y-auto divide-y divide-stone-100">
                    {data.junk_candidates.map((j) => (
                      <p key={j.id} className="truncate py-1 text-xs text-stone-500" data-testid={`recon-junk-${j.id}`}>{j.filename}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
              <h2 className="font-heading text-lg font-semibold text-stone-900">Documents per employee</h2>
              <Input value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} placeholder="Search name or email…"
                className="h-9 w-56 border-stone-300 bg-white text-sm" data-testid="recon-employee-search" />
            </div>
            <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm" data-testid="recon-table">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-stone-200 bg-secondary/60 text-xs uppercase tracking-wide text-stone-500">
                    <th className="px-6 py-3.5 font-medium">Employee</th>
                    <th className="px-4 py-3.5 font-medium">Certificates</th>
                    <th className="px-4 py-3.5 font-medium">Signed submissions</th>
                    <th className="px-4 py-3.5 font-medium">Other evidence</th>
                    <th className="px-4 py-3.5 font-medium">Employment records</th>
                    <th className="px-4 py-3.5 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-10 text-center text-sm text-stone-400" data-testid="recon-empty">No documents found.</td></tr>
                  )}
                  {employees.map((r, i) => (
                    <tr key={r.uid} className="border-b border-stone-100 last:border-0" data-testid={`recon-row-${i}`}>
                      <td className="px-6 py-3">
                        <Link to={`/team/${r.uid}`} className="font-medium text-stone-900 hover:text-primary hover:underline" data-testid={`recon-employee-link-${i}`}>{r.name || r.email}</Link>
                        {!r.active && <span className="ml-2 text-xs text-stone-400">inactive</span>}
                      </td>
                      <td className="px-4 py-3 text-stone-600" data-testid={`recon-certs-${i}`}>{r.certificates || "—"}</td>
                      <td className="px-4 py-3 font-medium text-[#1e8449]" data-testid={`recon-subs-${i}`}>{r.submissions || "—"}</td>
                      <td className="px-4 py-3 text-stone-600">{r.other_evidence || "—"}</td>
                      <td className="px-4 py-3 text-stone-600">{r.employment_records || "—"}</td>
                      <td className="px-4 py-3 font-medium text-stone-900" data-testid={`recon-total-${i}`}>{r.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
